import { Box, Text, VStack } from '@chakra-ui/react'
import { DotPhraseTemplate, WithId } from '@hb/shared'
import { useEffect, useRef } from 'react'

// Floating list of dot-phrase templates. When `coords` is provided it renders with
// `position: fixed` at the caret (so it appears next to the cursor and is never clipped by a
// scrolling textarea); otherwise it falls back to absolute, just below the input.
// `activeIndex` is the keyboard-highlighted row; `onHoverIndex` lets the mouse drive it too.
export const DotPhrasePicker = ({
  items,
  onSelect,
  coords,
  activeIndex = 0,
  onHoverIndex,
}: {
  items: WithId<DotPhraseTemplate>[]
  onSelect: (body: string) => void
  coords?: { top: number; left: number } | null
  activeIndex?: number
  onHoverIndex?: (index: number) => void
}) => {
  const activeRef = useRef<HTMLDivElement>(null)
  // Keep the highlighted row visible as the user arrows through a scrolled list.
  useEffect(() => {
    activeRef.current?.scrollIntoView?.({ block: 'nearest' })
  }, [activeIndex])

  return (
    <VStack
      position={coords ? 'fixed' : 'absolute'}
      top={coords ? `${coords.top}px` : '100%'}
      left={coords ? `${coords.left}px` : 0}
      zIndex={1500}
      bg="white"
      _dark={{ bg: 'gray.800', borderColor: 'gray.600' }}
      border="1px solid"
      borderColor="gray.200"
      borderRadius="md"
      boxShadow="md"
      maxH="220px"
      overflowY="auto"
      align="stretch"
      spacing={0}
      minW="220px">
      {items.map((t, i) => {
        const active = i === activeIndex
        return (
          <Box
            key={t.id}
            ref={active ? activeRef : undefined}
            px={3}
            py={2}
            cursor="pointer"
            bg={active ? 'green.100' : undefined}
            _dark={{ bg: active ? 'whiteAlpha.200' : undefined }}
            _hover={{ bg: active ? 'green.100' : 'green.50' }}
            onMouseEnter={() => onHoverIndex?.(i)}
            onMouseDown={e => {
              e.preventDefault()
              onSelect(t.body)
            }}>
            <Text fontSize="sm" fontWeight={600}>
              .{t.keyword}
            </Text>
            {t.description && (
              <Text fontSize="xs" color="gray.500" noOfLines={1}>
                {t.description}
              </Text>
            )}
          </Box>
        )
      })}
    </VStack>
  )
}
